import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import ArticleCard from "../common/ArticleCard";
import styles from "../../styles";
import { getArticlesContent } from "../../services/api";

export interface CarouselArticle {
  id: number;
  title: string;
  imageUrl: string | null;
  linkUrl: string | null;
}

// Fallback — identical to the original hard-coded list. Shown instantly and kept
// if the API is unreachable, so the carousel never renders empty.
const FALLBACK_ARTICLES: CarouselArticle[] = [
  { id: -1, imageUrl: "/assets/flights/turkey.jpg", linkUrl: null, title: "Travelling to Turkey? We have all the information that you need." },
  { id: -2, imageUrl: "/assets/flights/worldmap.jpg", linkUrl: null, title: "Having trouble applying for visit visas? Here is a checklist." },
  { id: -3, imageUrl: "/assets/flights/beach.jpg", linkUrl: null, title: "Want to plan a beach trip? Here are a few perfect destinations." },
];

const ArticleCarousel: React.FC<{ articles?: CarouselArticle[]; preview?: boolean }> = ({ articles: articlesProp, preview }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fetched, setFetched] = useState<CarouselArticle[]>(FALLBACK_ARTICLES);

  useEffect(() => {
    if (articlesProp) return; // preview / controlled mode: don't fetch
    let alive = true;
    getArticlesContent()
      .then((res) => {
        // Any successful response wins — including an empty one, so hiding every
        // article genuinely clears the carousel instead of resurrecting defaults.
        // The fallback is kept ONLY when the request itself fails (below).
        if (alive && Array.isArray(res?.articles)) {
          setFetched(res.articles as CarouselArticle[]);
        }
      })
      .catch((err) => console.warn("[ArticleCarousel] falling back to defaults:", err?.message));
    return () => { alive = false; };
  }, [articlesProp]);

  const articles = articlesProp ?? fetched;

  const handleScroll = () => {
    if (scrollRef.current) {
      const index = Math.round(
        scrollRef.current.scrollLeft / scrollRef.current.offsetWidth
      );
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Everything hidden → the carousel disappears on the live site.
  if (articles.length === 0) {
    return preview
      ? <div className="py-16 text-center text-sm text-gray-400">No active articles — the carousel is hidden on the live site.</div>
      : null;
  }

  const HeaderTag: any = preview ? "div" : motion.div;
  const headerAnim = preview ? {} : {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.5, ease: "easeOut" },
  };

  return (
    <section className={`${styles.boxWidth} ${styles.paddingY}`}>
      {/* Header */}
      <HeaderTag className="text-left space-y-1 mb-6" {...headerAnim}>
        <h2 className="text-black text-lg sm:text-xl md:text-2xl font-sora font-bold">
          Build memories for a lifetime
        </h2>
        <p className="text-sm sm:text-base text-heading">
          Updates on everything related to travel
        </p>
      </HeaderTag>

      {/* Carousel Container */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hidden
            snap-x snap-mandatory scroll-smooth py-4"
        >
          {articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={preview ? false : { opacity: 0, y: 24 }}
              whileInView={preview ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: Math.min(index, 4) * 0.07, ease: "easeOut" }}
              className="flex-none transition-transform duration-300 hover:-translate-y-1
                w-[300px] xs:w-[340px] sm:w-[400px] md:w-[380px] lg:w-[420px]
                snap-start"
            >
              <ArticleCard
                image={article.imageUrl ?? ""}
                title={article.title}
                link={article.linkUrl ?? undefined}
                className="transform transition-all duration-300 hover:shadow-lg"
              />
            </motion.div>
          ))}
        </div>

        {/* Scroll Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {articles.map((article, index) => (
            <button
              key={article.id}
              className={`h-1.5 rounded-full transition-all duration-300 ease-in-out
                ${
                  index === currentIndex
                    ? "w-6 bg-primary"
                    : "w-1.5 bg-gray-300 hover:bg-gray-400"
                }`}
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTo({
                    left: index * scrollRef.current.offsetWidth,
                    behavior: "smooth",
                  });
                }
              }}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ArticleCarousel;
